import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SubResponsible, SubResponsibleApiResponse } from '../models/sub-responsible.model';
import { environment } from '../../../environments/environment';
import { DocumentsService } from '../../core/services/documents.service';
import { FormDropdownService } from '../../core/services/form-dropdown.service';

@Injectable({
  providedIn: 'root'
})
export class SubResponsibleRegistryService {
  private baseUrl = `${environment.backofficeApiUrl}/api`;

  constructor(
    private http: HttpClient,
    private documentsService: DocumentsService,
    private formDropdownService: FormDropdownService
  ) {
    this.formDropdownService.preloadSupplierTypes().subscribe({
      error: (err) => console.error('Error preloading company type options:', err),
    });
  }

  /**
   * Export sub-responsabili in Excel
   */
  exportSubResponsiblesRegistry(subResponsibleId?: number | null): Observable<HttpResponse<Blob>> {
    return this.documentsService.exportRegistry('SUB_RESP_REF_01', null, null, subResponsibleId || null);
  }

  // =========================
  // GET - Get all sub-responsibles
  // =========================
  getSubResponsibles(): Observable<SubResponsibleApiResponse> {
    return this.http.get<SubResponsibleApiResponse>(`${this.baseUrl}/v1/sub-responsibles`);
  }

  // =========================
  // POST - Create sub-responsible
  // =========================
  createSubResponsible(subResponsible: SubResponsible): Observable<SubResponsibleApiResponse> {
    return this.http.post<SubResponsibleApiResponse>(`${this.baseUrl}/v1/sub-responsibles`, subResponsible, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // =========================
  // GET - Get sub-responsible by id
  // =========================
  getSubResponsible(id: string): Observable<SubResponsibleApiResponse> {
    return this.http.get<SubResponsibleApiResponse>(`${this.baseUrl}/v1/sub-responsibles/${id}`);
  }

  // =========================
  // DELETE - Delete sub-responsible
  // =========================
  deleteSubResponsible(id: string): Observable<SubResponsibleApiResponse> {
    return this.http.delete<SubResponsibleApiResponse>(`${this.baseUrl}/v1/sub-responsibles/${id}`);
  }

  // =========================
  // PATCH - Partial update
  // =========================
  updateSubResponsiblePatch(id: string, subResponsibleData: Partial<SubResponsible>): Observable<SubResponsibleApiResponse> {
    return this.http.patch<SubResponsibleApiResponse>(`${this.baseUrl}/v1/sub-responsibles/${id}`, subResponsibleData);
  }

  // =========================
  // PUT - Full update
  // =========================
  updateSubResponsiblePut(id: string, subResponsible: SubResponsible): Observable<SubResponsibleApiResponse> {
    return this.http.put<SubResponsibleApiResponse>(`${this.baseUrl}/v1/sub-responsibles/${id}`, subResponsible);
  }

  private formatCompanyType(companyType?: string | null): string {
    if (!companyType) return '';

    return this.formDropdownService.decodeSupplierTypeSync(companyType);
  }

  // =========================
  // UTILITY - Map sub-responsibles data for display
  // =========================
  /**
   * Mappa i dati dei sub-responsabili per la visualizzazione nella tabella
   * Centralizza la logica per evitare duplicazioni nei componenti
   */
  mapSubResponsiblesForDisplay(subResponsibles: any[]): any[] {
    return subResponsibles.map((subResp: any) => ({
      id: subResp.subResponsibleId || '',
      businessName: subResp.businessName || '',
      rawBusinessName: subResp.businessName || '',
      companyType: subResp.companyType || '',
      companyTypeLabel: this.formatCompanyType(subResp.companyType),
      headquarters: subResp.headquarters || '',
      email: subResp.email || '',
      dataProcessingLocation: subResp.dataProcessingLocation || '',
      createdAt: subResp.createdAt ? new Date(subResp.createdAt).toLocaleDateString('it-IT') : '',
      status: subResp.status || 'ACTIVE',
    }));
  }

  /**
   * Estrae l'array di sub-responsabili dalla risposta API
   * Gestisce diversi formati di risposta (paginata, array, singolo oggetto)
   */
  extractSubResponsiblesArray(response: SubResponsibleApiResponse): any[] {
    if (!response?.status || !response?.data) {
      return [];
    }

    let dataArray: any[] = [];
    
    // Check if data has content property (paginated response)
    if ('content' in response.data) {
      dataArray = Array.isArray(response.data.content) ? response.data.content : [response.data.content];
    } else if (Array.isArray(response.data)) {
      dataArray = response.data;
    } else {
      dataArray = [response.data];
    }

    return dataArray;
  }
}
